import { useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useRecoilValue } from "recoil";

import {
  activeProcessSF,
  getActiveProcessKey,
  handleProcess,
  IRydeProcess,
  isProcessCanceledByUser,
  Log,
  RESTORE_SUMMARY_PATTERN,
  RydeProcessTitle,
  RydeUiUnit,
  useProcesses,
} from "@ryde/shared";

export const useWatchBackupRestore = ({ entity }: { entity: RydeUiUnit }) => {
  const { t } = useTranslation();
  const { stopProcessPolling } = useProcesses();
  const backupRestoreProcessKey = getActiveProcessKey(
    RydeProcessTitle.BackupRestore,
    entity,
  );
  const backupRestoreProcess = useRecoilValue(
    activeProcessSF(backupRestoreProcessKey),
  );

  const onComplete = useCallback(
    (process: IRydeProcess) => {
      if (isProcessCanceledByUser(process)) {
        return;
      }

      const summary = process.logs?.find(line => line.includes("inserted"));
      const counts = summary?.match(RESTORE_SUMMARY_PATTERN);
      Log.successAndToast(
        t(
          "backup.restore.success",
          "Restore complete: {{inserted}} inserted, {{updated}} updated, {{deleted}} deleted, {{skipped}} skipped",
          {
            inserted: counts ? Number(counts[1]) : 0,
            updated: counts ? Number(counts[2]) : 0,
            deleted: counts ? Number(counts[3]) : 0,
            skipped: counts ? Number(counts[4]) : 0,
          },
        ),
      );
      // Skipped rows are shown in the process drawer's info, so no extra per-item toast here.
    },
    [t],
  );

  useEffect(() => {
    handleProcess({
      onComplete,
      entity,
      processTitle: RydeProcessTitle.BackupRestore,
      process: backupRestoreProcess,
      stopProcessPolling,
      translationFunction: t,
    });
  }, [backupRestoreProcess, entity, onComplete, stopProcessPolling, t]);
};
